import React from 'react';
import { cn } from '../../../../lib/utils';
import { useStatusBarInfo } from '../../../../hooks/useStatusBarInfo';
import { useViewport } from '../../../../contexts/ViewportContext';

/**
 * A skeleton loader for mobile task kanban view
 * Matches the desktop skeleton design but shows only one column
 */
export function MobileTaskSkeleton() {
  // Blue border for all task cards
  const columnColor = 'border-blue-300';

  // Shimmer animation class for all elements
  const shimmerClass = 'animate-pulse';

  // Get status bar info for mobile native apps
  const { isNativeApp, statusBarHeight } = useStatusBarInfo();
  const { isMobile } = useViewport();

  // Calculate top padding based on status bar height
  const topPadding = isNativeApp && isMobile ? `${statusBarHeight}px` : '0px';

  return (
    <div
      className="p-4 min-h-full bg-[var(--content-bg-color)]"
      style={{ paddingTop: `calc(1rem + ${topPadding})` }}
    >
      {/* Column header */}
      <div className="flex items-center justify-between mb-3 p-2">
        <div className="flex items-center gap-2">
          <div className={cn('w-24 h-6 bg-gray-200 rounded', shimmerClass)} />
          <div className={cn('w-6 h-6 bg-gray-200 rounded-full', shimmerClass)} />
        </div>
        <div className="flex gap-1">
          <div className={cn('w-6 h-6 rounded-full bg-gray-200', shimmerClass)} />
          <div className={cn('w-6 h-6 rounded-full bg-gray-200', shimmerClass)} />
        </div>
      </div>

      {/* Task cards container */}
      <div className="space-y-3">
        {/* Generate 3 task cards */}
        {[...Array(3)].map((_, cardIndex) => (
          <div
            key={cardIndex}
            className={cn('p-4 bg-white rounded-md shadow-sm mb-3 border-l-[5px]', columnColor)}
          >
            {/* Task title */}
            <div className={cn('h-5 bg-gray-50 rounded w-full mb-2', shimmerClass)} />

            {/* Task description (for some cards) */}
            {cardIndex % 2 === 0 && (
              <div className="mb-3 space-y-1">
                <div className={cn('h-3 bg-gray-50 rounded w-full', shimmerClass)} />
                <div className={cn('h-3 bg-gray-50 rounded w-5/6', shimmerClass)} />
              </div>
            )}

            {/* Bottom row with deadline and assignee */}
            <div className="flex items-center justify-between mt-2">
              <div className={cn('h-4 bg-gray-50 rounded w-20', shimmerClass)} />

              {/* Assignee avatar (sometimes) */}
              {cardIndex % 3 !== 0 && (
                <div className={cn('w-6 h-6 rounded-full bg-gray-50', shimmerClass)} />
              )}
            </div>

            {/* Tags (sometimes) */}
            {cardIndex % 2 === 1 && (
              <div className="flex flex-wrap gap-1 mt-2">
                <div className={cn('h-5 bg-gray-50 rounded-full w-16', shimmerClass)} />
                <div className={cn('h-5 bg-gray-50 rounded-full w-12', shimmerClass)} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
